import { getSpecificationPropertyObject } from '../../../utils/utils';
import { SpecificationPropertyObject } from './specification-property-object.model';
import { DropdownWidget, RadioButtonGroupWidget, StringResource } from './module-control-specification.model';
import { ModuleShareRead, ModuleSubmissionProducerRead, VersionReadModel } from './module-v1-read.model';
import { ModuleSpecification, SpecProperty } from './module-specification.model';
import { IVersionReadModel, ModuleParameter } from './parameter.model';
import { ModuleInputType } from '../enums/module-input-type.enum';

export interface IModuleModel {
    id?: string;
    title?: string;
    latestPublicVersion?: VersionReadModel;
    draftVersion?: VersionReadModel;
    selectedModuleVersion?: VersionReadModel;
    shares?: ModuleShareRead[];
    producerId?: string;
    submissions?: ModuleSubmissionProducerRead[];
}

export class ModuleVersion {
    inputParameters: ModuleParameter[] = [];
    outputParameters: ModuleParameter[] = [];

    constructor(
        public versionReadModel: IVersionReadModel,
        public module: IModuleModel,
    ) {
        this.addInputParameters(versionReadModel.specification);
        this.addOutputParameters(versionReadModel.specification);
    }

    get specification(): ModuleSpecification | undefined {
        return this.versionReadModel.specification;
    }

    get title(): string | undefined {
        return this.module.title;
    }

    get id(): string {
        return this.versionReadModel.id;
    }

    get isOpenSource(): boolean {
        return this.versionReadModel.isOpenSource;
    }

    get moduleId(): string | undefined {
        return this.module.id;
    }

    get versionNumber(): number {
        return this.versionReadModel.versionNumber;
    }

    get latestPublicVersion(): VersionReadModel | undefined {
        return this.module.latestPublicVersion;
    }

    get draftVersion(): VersionReadModel | undefined {
        return this.module.draftVersion;
    }

    get hasDraftVersion(): boolean {
        return this.draftVersion != null;
    }

    isMine(myUserId?: string) {
        return myUserId != null && this.module.producerId === myUserId;
    }

    isSharedWithMe(myUserId?: string) {
        let isShared = false;
        if (this.module.shares && this.module.shares.length > 0 && myUserId != null) {
            isShared = this.module.shares.filter((user: ModuleShareRead) => user.userId === myUserId).length > 0;
        }

        return isShared;
    }

    getInputParameter(inputKey: string): ModuleParameter | undefined {
        return this.inputParameters.find((p: ModuleParameter) => inputKey === p.key);
    }

    getOutputParameter(outputKey: string): ModuleParameter | undefined {
        return this.outputParameters.find((p: ModuleParameter) => outputKey === p.key);
    }

    getOutputParameterByType(type: ModuleInputType): ModuleParameter | undefined {
        return this.outputParameters.find((p: ModuleParameter) => type === p.type);
    }

    static getParameterTypeFromSpec(specProperty: SpecProperty<unknown>): string | undefined {
        let type: string | undefined;
        Object.keys(specProperty).forEach((key: string) => {
            if (key.indexOf('type:') > -1) {
                type = key;
            }
        });

        return type;
    }

    static mapTypeToModuleInputType(type: string): ModuleInputType {
        type = type.toLowerCase();
        const typeToModuleTypeMap: Map<string, ModuleInputType> = new Map<string, ModuleInputType>([
            ['type:integer', ModuleInputType.Integer],
            ['type:number', ModuleInputType.Number],
            ['type:string', ModuleInputType.String],
            ['type:file', ModuleInputType.File],
            ['type:range', ModuleInputType.Range],
            ['type:choice_binary', ModuleInputType.ChoiceBinary],
            ['type:choice_single', ModuleInputType.ChoiceSingle],
            ['type:list[file]', ModuleInputType.ListFile],
            ['type:list[string]', ModuleInputType.ListFile],
        ]);

        const mappedType: ModuleInputType | undefined = typeToModuleTypeMap.get(type);

        if (mappedType == null) {
            throw new Error('Type ' + type + ' could not be mapped to ModuleInputType');
        }

        return mappedType;
    }

    private addInputParameters(specification: ModuleSpecification): void {
        this.addParameters(specification, 'inputs');
    }

    private addOutputParameters(specification: ModuleSpecification): void {
        this.addParameters(specification, 'outputs');
    }

    private addParameters(specification: ModuleSpecification, inputOutput: 'inputs' | 'outputs'): void {
        const parameters: ModuleParameter[] = [];

        // Add inputs/outputs
        if (specification != null && specification.spec[inputOutput] != null) {
            for (const [key, value] of Object.entries(specification.spec[inputOutput])) {
                const type: string | undefined = ModuleVersion.getParameterTypeFromSpec(value as SpecProperty<unknown>);
                if (type == null) {
                    throw new Error('Type cannot be null in ModuleParameter generation');
                }

                let label = key;
                let description;

                // only set label and description if the input/output parameter is available (as key) in 'ui' object
                if (specification.ui != null && specification.ui[inputOutput] != null && specification.ui[inputOutput][key] != null) {
                    label = specification.ui[inputOutput][key].label;
                    description = specification.ui[inputOutput][key].description;
                }

                // Choice_single, choice_multiple have a set of values which the user can choose of. They will be appended to the ModuleParameter class
                const specProp: SpecificationPropertyObject | undefined = getSpecificationPropertyObject(key, this.versionReadModel.specification);

                let selectionItems: StringResource[] | undefined = undefined;
                if (specProp != null && specProp.widgetSpecificUiProperty != null) {
                    selectionItems = (specProp.widgetSpecificUiProperty as DropdownWidget | RadioButtonGroupWidget).items;
                }

                const parameter = new ModuleParameter(key, label, ModuleVersion.mapTypeToModuleInputType(type), description, selectionItems);
                parameters.push(parameter);
            }
        } else {
            throw new Error('The "specification"-parameter in ModuleVersion cannot be empty.');
        }

        if (inputOutput === 'inputs') {
            this.inputParameters = parameters;
        } else if (inputOutput === 'outputs') {
            this.outputParameters = parameters;
        }
    }
}
